import React, {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useCallback,
  useMemo
} from 'react';
import type { RowSelectionState, Table } from '@tanstack/react-table';
import ButtonGroup from 'atom-components/button-group';
import { pluralise } from 'helpers/string.helpers';

type Props<TData> = {
  tableInstance: Table<TData>;
  rowSelection: RowSelectionState;
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
};

export default function ClearSelectionButtons<TData extends object>({
  tableInstance,
  rowSelection,
  setRowSelection
}: Props<TData>): ReactElement {
  /**
   * Returns the total number of rows that have been selected, both visible and hidden rows.
   */
  const totalRowSelectionCount: number = useMemo(
    () => Object.keys(rowSelection).length,
    [rowSelection]
  );

  /**
   * Returns the number of rows that have been selected by are no longer visible on the screen (due
   * to reducing the page size, navigating to another page, or applying a filter).
   */
  const hiddenRowSelectionCount = useCallback(
    (): number =>
      Object.keys(rowSelection).filter(
        (selectedRowId) =>
          !tableInstance
            .getRowModel()
            .rows.map((row) => row.id)
            .includes(selectedRowId)
      ).length,
    [rowSelection, tableInstance]
  );

  /**
   * Clears only rows that are no longer visible on the screen for the row selection, leaving
   * visible rows in the row selection object.
   */
  const clearHiddenRowSelections = useCallback((): void => {
    const newRowSelection = Object.keys(rowSelection).reduce(
      (accumulator: RowSelectionState, currentValue: string) => {
        const rowExists = tableInstance.getRowModel().rows.find((row) => row.id === currentValue);
        if (rowExists) {
          return {
            ...accumulator,
            [currentValue]: true
          };
        }
        return { ...accumulator };
      },
      {}
    );
    setRowSelection(newRowSelection);
  }, [rowSelection, setRowSelection, tableInstance]);

  return (
    <ButtonGroup leftButtons>
      <ButtonGroup.ClearButton
        onClick={(): void => tableInstance.resetRowSelection()}
        disabled={totalRowSelectionCount === 0}
      >
        Clear {totalRowSelectionCount} selected {pluralise('item', totalRowSelectionCount)}
      </ButtonGroup.ClearButton>
      <ButtonGroup.ClearButton
        onClick={(): void => clearHiddenRowSelections()}
        disabled={hiddenRowSelectionCount() === 0}
      >
        Clear {hiddenRowSelectionCount()} hidden {pluralise('selection', hiddenRowSelectionCount())}
      </ButtonGroup.ClearButton>
    </ButtonGroup>
  );
}
